import React, { useContext, useMemo } from 'react'
import { DataCard, DataColumn, DataGrid } from '@/components/DataCard/DataCard'
import {
  FormCheckboxField,
  FormDateField,
  FormField,
  FormFieldLayout,
  FormReadonlyField,
  FormSelectField,
} from '@/components/Form/FormField'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { ButtonGroup } from '@/components/ui/button-group'
import { Checkbox } from '@/components/ui/checkbox'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { Spinner } from '@/components/ui/spinner'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { ClientLeadTeamDto } from 'api/Clients/Types/ClientDetailedDto'
import { ClientType } from 'api/Clients/Types/ClientType'
import { Language } from 'api/Clients/Types/Language'
import { SalesStatus } from 'api/Clients/Types/SalesStatus'
import { CountryDto } from 'api/Identity/Types/Country'
import { PermissionDto } from 'api/Identity/Types/PermissionDto'
import { PermissionsEnum } from 'api/Identity/Types/PermissionsEnum'
import { SignInResponse } from 'api/Identity/Types/SignInResponse'
import { UserStatus } from 'api/Identity/Types/UserResponse'
import { LeadDetailsDto } from 'api/Leads/Types/LeadDetailsDto'
import { UpdateLead } from 'api/Leads/Types/UpdateLead'
import { VerifiableActionDto } from 'api/Leads/Types/VerifiableActionDto'
import { Select } from 'components/Select'
import { successToast, ToastContext } from 'components/Toast/Toast'
import { Form, FormikErrors, FormikProps, withFormik } from 'formik'
import { useCountriesToCountryCodes } from 'hooks/useCountriesToCountryCodes'
import { CheckCheck, ChevronDown, CircleAlert } from 'lucide-react'
import { useApiClient } from 'utils/api/ApiClient'
import { LeadsApiClient } from 'utils/api/leadsApi'
import { createDateAsUTC, formatLocalDateUTC, stripTimeFromFormat } from 'utils/date'
import { FormSubmitValues } from 'utils/formValidation'
import { hasPermission } from 'utils/permissionsValidator'

import { NameDto } from '../../../../api/Common/Types'

interface LeadDetailsFormValues {
  id: string
  title: string
  firstName: string
  middleName: string
  lastName: string
  nativeName?: string | null
  typeId: number
  email: string
  phoneNumber: string
  leadSource: {
    id: number
    name: string
  }
  country: {
    name: string
    id: string
  }
  countryId: string
  birthDay: string | null
  languageId: string
  tickmillCompanyId: number
  emailVerified: boolean
  phoneVerified: boolean
  created: string
  updated: string
  address: string | null
  city: string | null
  isUsCitizen: boolean
  state: string | null
  countryCode: string | null
  postalCode: string | null
  teams: ClientLeadTeamDto[]
  type: {
    id: number
    name: string
  }
  salesStatusId: number
  tickmillProducts: NameDto[]
  assignee: {
    id: string
    name: string
    status?: {
      id: number
      name: string
    }
  } | null
  etdAssignee: {
    id: string
    name: string
    status?: {
      id: number
      name: string
    }
  } | null
  gender: {
    id: 1 | 2
    name: 'Male' | 'Female'
  } | null
  preferredCurrency: {
    name: string
    id: string
  } | null
}

const LockedStatusBadge = () => (
  <Button variant='destructive' size='icon-xs'>
    <Tooltip>
      <TooltipTrigger>
        <CircleAlert />
      </TooltipTrigger>
      <TooltipContent side='top'>User Locked</TooltipContent>
    </Tooltip>
  </Button>
)

const LeadDetailsFormUI: React.FC<FormikProps<LeadDetailsFormValues> & OuterProps> = (props) => {
  const setToast = useContext(ToastContext)
  const leadsApiClient = useApiClient(LeadsApiClient)

  const {
    values,
    user,
    countries,
    languages,
    clientTypes,
    resetForm,
    handleSubmit,
    isEdit,
    setEdit,
    setFieldValue,
    syncHubspot,
    isConverted,
    salesStatuses,
    permissions,
    showVerificationsModal,
  } = props

  const sendResetEmailVerificationRequest = async () => {
    await leadsApiClient.sendLeadEmailVerificationRequest(props.data.id)
    setToast(successToast('Email sent.'))
  }

  const uniqueCountryCodes = useCountriesToCountryCodes(countries)

  const countryCodeOptions = useMemo(
    () =>
      uniqueCountryCodes.map((countryCode) => ({
        key: countryCode,
        value: countryCode,
        label: countryCode,
        text: countryCode,
      })),
    [uniqueCountryCodes]
  )

  const sortedTeams = useMemo(
    () => [...(values.teams ?? [])].sort((team) => (team.isPrimary ? -1 : 1)),
    [values.teams]
  )

  const renderMenu = () => (
    <>
      <DropdownMenuItem
        onSelect={() => props.setAuditLogModal(true)}
        data-test='audit-log-menu-button'
      >
        Audit Log
      </DropdownMenuItem>
      <DropdownMenuSub>
        <DropdownMenuSubTrigger>Verification</DropdownMenuSubTrigger>
        <DropdownMenuSubContent>
          <DropdownMenuItem onSelect={showVerificationsModal}>History</DropdownMenuItem>
          <DropdownMenuItem
            onSelect={sendResetEmailVerificationRequest}
            disabled={
              isConverted || !hasPermission(permissions, PermissionsEnum.CanCreateEmailVerification)
            }
            data-test='resent-email-verification'
          >
            Resent Email Verification
          </DropdownMenuItem>
        </DropdownMenuSubContent>
      </DropdownMenuSub>
      <DropdownMenuSub>
        <DropdownMenuSubTrigger>Hubspot</DropdownMenuSubTrigger>
        <DropdownMenuSubContent>
          <DropdownMenuItem onSelect={syncHubspot} data-test='sync-menu-button'>
            Sync
          </DropdownMenuItem>
        </DropdownMenuSubContent>
      </DropdownMenuSub>
      {hasPermission(permissions, PermissionsEnum.CanAssignSglToLead) && (
        <DropdownMenuItem
          onSelect={() => props.setAssignSGLModal(true)}
          data-test='assign-sgl-menu-button'
        >
          Assign SGL
        </DropdownMenuItem>
      )}
    </>
  )

  const handleSubmitForm = () => {
    setEdit(false)
    handleSubmit()
  }

  const handleCancelForm = () => {
    setEdit(false)
    resetForm()
  }

  const cfdAssigneeWarning =
    values.assignee?.status?.id === UserStatus.Locked ? <LockedStatusBadge /> : undefined

  return (
    <Form>
      <DataGrid
        rightAddon={
          <ButtonGroup>
            {hasPermission(permissions, PermissionsEnum.CanEditLeads) &&
              (!isEdit ? (
                <Button
                  type='button'
                  disabled={isConverted}
                  onClick={() => setEdit(true)}
                  data-test='edit-button'
                >
                  Edit
                </Button>
              ) : (
                <>
                  <Button
                    type='button'
                    variant='outline'
                    onClick={handleCancelForm}
                    disabled={props.isSubmitting}
                    data-test='cancel-button'
                  >
                    Cancel
                  </Button>
                  <Button
                    type='button'
                    onClick={handleSubmitForm}
                    disabled={props.isSubmitting}
                    data-test='save-button'
                  >
                    Save
                    {props.isSubmitting && <Spinner data-icon='inline-start' />}
                  </Button>
                </>
              ))}
            <DropdownMenu>
              <DropdownMenuTrigger
                render={
                  <Button
                    type='button'
                    aria-label='More options'
                    variant='outline'
                    size='icon'
                    data-test='more-button'
                  />
                }
              >
                <ChevronDown />
              </DropdownMenuTrigger>
              <DropdownMenuContent align='end'>{renderMenu()}</DropdownMenuContent>
            </DropdownMenu>
          </ButtonGroup>
        }
      >
        <DataColumn>
          <DataCard columns={2} title='Main Information'>
            <FormField label='First Name' name='firstName' readOnly={!isEdit} />
            <FormField label='Middle Name' name='middleName' readOnly={!isEdit} />
            <FormField label='Last Name' name='lastName' readOnly={!isEdit} />
            <FormField label='Native Name' name='nativeName' readOnly={!isEdit} />
            <FormReadonlyField label='Gender' value={values.gender?.name ?? ''} />
            <FormSelectField
              name='typeId'
              options={clientTypes}
              label='Type'
              readOnly={!isEdit}
              onChange={(value) => setFieldValue('typeId', Number(value))}
            />

            <FormDateField
              label='Birthday'
              dateFormatType={stripTimeFromFormat(user?.dateFormatType.name)}
              name='birthDay'
              readOnly={!isEdit}
            />
            <FormReadonlyField
              label='CFD Assignee'
              value={values.assignee?.name ?? ''}
              rightAddon={cfdAssigneeWarning}
            />
            <FormReadonlyField label='ETD Assignee' value={values.etdAssignee?.name ?? ''} />
          </DataCard>

          <DataCard title='Classification'>
            <FormCheckboxField label='Is US Citizen' value={values.isUsCitizen} readOnly />
          </DataCard>

          <DataCard columns={2} title='More'>
            <FormReadonlyField
              label='Preferred Currency'
              value={values.preferredCurrency?.id ?? ''}
            />
            <FormReadonlyField label='Lead Source' value={values.leadSource.name} />
            <FormSelectField
              name='salesStatusId'
              options={salesStatuses}
              label='Sales Status'
              readOnly={!isEdit}
              onChange={(value) => setFieldValue('salesStatusId', value)}
            />

            <FormFieldLayout name='tickmillProducts' label='Products'>
              <div className='flex w-full flex-wrap gap-2'>
                {values.tickmillProducts.map((product) => (
                  <Badge key={product.id} variant='neutral'>
                    {product.name}
                  </Badge>
                ))}
              </div>
            </FormFieldLayout>
          </DataCard>

          <DataCard title='Activity' columns={2}>
            <FormReadonlyField
              label='Date Created'
              value={formatLocalDateUTC(props.data.created, true)?.toString()}
            />
            <FormReadonlyField
              label='Date Modified'
              value={formatLocalDateUTC(props.data.updated, true)?.toString()}
            />
            <FormReadonlyField
              label='Verification Email Send Date'
              value={formatLocalDateUTC(props.leadVerifiableAction.emailSentAt, true)?.toString()}
            />
            <FormReadonlyField
              label='Verified Email Date'
              value={formatLocalDateUTC(props.leadVerifiableAction.completedAt, true)?.toString()}
            />
            <FormReadonlyField
              label='Expiration Email Date'
              value={formatLocalDateUTC(props.leadVerifiableAction.expiryAt, true)?.toString()}
            />
          </DataCard>
        </DataColumn>

        <DataColumn>
          <DataCard title='Contact'>
            <FormFieldLayout label='Phone Number'>
              <Select
                name='countryCode'
                options={countryCodeOptions}
                value={values.countryCode ?? ''}
                onChange={(value) => setFieldValue('countryCode', value)}
                className='w-full max-w-22'
                showSearch
                readOnly={!isEdit}
              />
              <Input
                name='phoneNumber'
                readOnly={!isEdit}
                className='w-full'
                value={values.phoneNumber ?? ''}
                onChange={(event) => setFieldValue('phoneNumber', event.target.value)}
              />
              {values.phoneVerified && <CheckCheck className='pl-1' color='green' />}
            </FormFieldLayout>

            <FormField
              label='Email Address'
              name='email'
              readOnly={!isEdit}
              rightAddon={values.emailVerified && <CheckCheck className='pl-1' color='green' />}
            />

            <FormSelectField
              options={languages}
              name='languageId'
              label='Communication Language'
              readOnly={!isEdit}
              onChange={(value) => setFieldValue('languageId', value)}
            />
          </DataCard>

          <DataCard columns={2} title='Address'>
            <FormReadonlyField label='Street' value={values.address?.toString()} />
            <FormSelectField
              options={countries}
              label='Country'
              showSearch
              readOnly={!isEdit}
              name='countryId'
              onChange={(value) => setFieldValue('countryId', value)}
            />

            <FormReadonlyField label='State' value={values.state?.toString()} />
            <FormReadonlyField label='Postal Code' value={values.postalCode?.toString()} />
          </DataCard>

          <DataCard title='Teams'>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Team Name</TableHead>
                  <TableHead>Primary</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedTeams.map((team) => (
                  <TableRow key={team.id}>
                    <TableCell>{team.name}</TableCell>
                    <TableCell>
                      <Checkbox checked={team.isPrimary} readOnly />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </DataCard>
        </DataColumn>
      </DataGrid>
    </Form>
  )
}

interface OuterProps {
  data: LeadDetailsDto
  leadVerifiableAction: VerifiableActionDto
  permissions: PermissionDto[]
  clientTypes: ClientType[]
  user?: SignInResponse
  countries: CountryDto[]
  languages: Language[]
  salesStatuses: SalesStatus[]
  isConverted: boolean
  tickmillProducts: NameDto[]

  setAuditLogModal(value: boolean): void
  setAssignSGLModal(value: boolean): void
  syncHubspot(): void
  isEdit: boolean
  setEdit(value: boolean): void
  onSubmit(values: FormSubmitValues<UpdateLead>): Promise<void>
  showVerificationsModal(): void
}

const dataToSubmit = (values: LeadDetailsFormValues): UpdateLead => {
  const birthDay = values.birthDay ? createDateAsUTC(values.birthDay).toISOString() : null

  return {
    id: values.id,
    email: values.email,
    title: values.title,
    countryCode: values.countryCode || '',
    phoneNumber: values.phoneNumber,
    typeId: values.typeId,
    firstName: values.firstName,
    middleName: values.middleName,
    lastName: values.lastName,
    nativeName: values.nativeName || '',
    birthDay: birthDay || null,
    countryId: values.countryId,
    languageId: values.languageId,
    salesStatusId: values.salesStatusId,
    productIds: values.tickmillProducts.map((x) => x.id),
  }
}

export const LeadDetailsForm = withFormik<OuterProps, LeadDetailsFormValues>({
  mapPropsToValues: ({ data }) => {
    return {
      id: data.id,
      title: data.title,
      firstName: data.firstName,
      middleName: data.middleName || '',
      salesStatusId: data.salesStatus.id,
      typeId: data.type.id,
      email: data.email,
      phoneNumber: data.phoneNumber,
      country: data.country,
      birthDay: data.birthDay || null,
      languageId: data.language.id,
      tickmillCompanyId: data.tickmillCompany.id,
      emailVerified: data.emailVerified,
      phoneVerified: data.phoneVerified,
      created: data.created,
      updated: data.updated,
      lastName: data.lastName,
      nativeName: data.nativeName || '',
      address: data.address || '',
      city: data.city || '',
      isUsCitizen: data.isUsCitizen,
      state: data.state || '',
      countryCode: data.countryCode,
      postalCode: data.postalCode || '',
      gender: data.gender,
      countryId: data.country.id,
      teams: data.teams,
      leadSource: data.leadSource,
      type: data.type,
      assignee: data.assignee,
      preferredCurrency: data.preferredCurrency,
      tickmillProducts: data.products,
      etdAssignee: data.etdAssignee,
    }
  },
  handleSubmit: async (values, { props, setSubmitting }) => {
    try {
      await props.onSubmit(dataToSubmit(values))
      setSubmitting(false)
    } finally {
      setSubmitting(false)
    }
  },
  validate: () => {
    const errors: FormikErrors<LeadDetailsFormValues> = {}
    return errors
  },
  enableReinitialize: true,
})(LeadDetailsFormUI)
